export interface DisplayUnits {
  length: 'm' | 'ft';
  speed: 'mps' | 'kmh';
}

export type LengthUnit = DisplayUnits['length'];
export type SpeedUnit = DisplayUnits['speed'];

export const METERS_TO_FEET = 3.28084;
export const MPS_TO_KMH = 3.6;

/** Convert an SI length without rounding; presentation rounding belongs in formatAltitude. */
export const metersToDisplay = (meters: number, unit: LengthUnit): number =>
  unit === 'ft' ? meters * METERS_TO_FEET : meters;

/** Convert an SI speed without rounding; presentation rounding belongs in formatSpeed. */
export const mpsToDisplay = (metersPerSecond: number, unit: SpeedUnit): number =>
  unit === 'kmh' ? metersPerSecond * MPS_TO_KMH : metersPerSecond;

export const formatAltitude = (meters: number, unit: LengthUnit): string => {
  if (!Number.isFinite(meters)) return `--.- ${unit}`;
  return `${metersToDisplay(meters, unit).toFixed(1)} ${unit}`;
};

export const formatSpeed = (metersPerSecond: number, unit: SpeedUnit): string => {
  const label = unit === 'mps' ? 'm/s' : 'km/h';
  if (!Number.isFinite(metersPerSecond)) return `--.- ${label}`;
  return `${mpsToDisplay(metersPerSecond, unit).toFixed(1)} ${label}`;
};

export const formatHeadingDeg = (degrees: number): string => {
  if (!Number.isFinite(degrees)) return '---°';
  const normalized = ((degrees % 360) + 360) % 360;
  const rounded = Math.round(normalized) % 360;
  return `${String(rounded).padStart(3, '0')}°`;
};

export const formatVoltage = (volts: number): string =>
  Number.isFinite(volts) ? `${volts.toFixed(1)} V` : '--.- V';

/** Format a 0..1 fraction as a clamped, whole-number percentage. */
export const formatPercent = (fraction: number): string => {
  if (!Number.isFinite(fraction)) return '--%';
  return `${Math.round(Math.min(1, Math.max(0, fraction)) * 100)}%`;
};

/** Format seconds as MM:SS.mmm. Null and non-finite values use an OSD placeholder. */
export const formatDuration = (seconds: number | null | undefined): string => {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return '--:--.---';
  const totalMilliseconds = Math.round(Math.max(0, seconds) * 1000);
  const minutes = Math.floor(totalMilliseconds / 60_000);
  const remaining = totalMilliseconds % 60_000;
  const wholeSeconds = Math.floor(remaining / 1000);
  const milliseconds = remaining % 1000;
  return `${String(minutes).padStart(2, '0')}:${String(wholeSeconds).padStart(2, '0')}.${String(milliseconds).padStart(3, '0')}`;
};
